import React, { useMemo } from 'react'
import { useGeneralSchemaQuery } from './hooks/use-general-schema-query'
import { useMultiStepFormQuery } from './hooks/use-multi-step-form-query'
import { usePageSpecificSchemaQuery } from './hooks/use-page-specific-schema-query'
import { usePostHeroDefaultQuery } from './hooks/use-post-hero-default-query'
import favicon from './images/favicon.png'
import { useWhatToExpectQuery } from './hooks/use-what-to-expect-query'
import { useHeaderQuery } from './hooks/use-header-query'
import { useInteriorHeroQuery } from './hooks/use-interior-hero-query'
import { useInteriorAccordionQuery } from './hooks/use-interior-accordion-query'
import { useCallout2Query } from './hooks/use-callout2-query'
import { useXPinsQuery } from './hooks/use-x-pins-query'
import { useRebatesQuery } from './hooks/use-rebates-query'
import { useTestimonialsSectionQuery } from './hooks/use-testimonials-section-query'
import { useCallout1Query } from './hooks/use-callout1-query'
import { useContactQuery } from './hooks/use-contact-query'
import { ReactSVG } from 'react-svg'
import { useFooterQuery } from './hooks/use-footer-query'
import { useHeroHomeQuery } from './hooks/use-hero-home-query'
import { useAwardsQuery } from './hooks/use-awards-query'
import { useOurServicesQuery } from './hooks/use-our-services-query'
import { useMeetOurTeamQuery } from './hooks/use-meet-our-team-query'
import { useWelcomeQuery } from './hooks/use-welcome-query'
import { usePartnershipQuery } from './hooks/use-partnership-query'
import { useLatestNewsQuery } from './hooks/use-latest-news-query'
import { useStickyCalloutQuery } from './hooks/use-sticky-callout-query'

export function GeneralSchemaFunction() {
  const { wp, site, query1, forRatingValuePart1, forRatingValuePart2 } = useGeneralSchemaQuery()

  const data = wp.nexvelSchemaMarkup.nexvelschema

  return {
    services: data.services,
    cities: data.cities,
    businessName: data.businessName,
    siteUrl: site.siteMetadata.siteUrl,
    businessType: data.businessType,
    logo: data.logo.localFile.publicURL,
    image: data.image.localFile.publicURL,
    businessDescription: data.describeYourBusiness,
    phoneNumber: data.phoneNumber,
    address: data.streetAddress,
    city: data.city,
    state: data.state,
    zip: data.zipCode,
    openSunday: data.hoursOfOperation.sunday.areYouOpen,
    sundayClosingTime: `${data.hoursOfOperation.sunday.close.closeHour}:${data.hoursOfOperation.sunday.close.closeMinute} ${data.hoursOfOperation.sunday.close.amorpm}`,
    sundayOpeningTime: `${data.hoursOfOperation.sunday.open.openHour}:${data.hoursOfOperation.sunday.open.openMinute} ${data.hoursOfOperation.sunday.open.amorpm}`,
    openMonday: data.hoursOfOperation.monday.areYouOpen,
    mondayClosingTime: `${data.hoursOfOperation.monday.close.closeHour}:${data.hoursOfOperation.monday.close.closeMinute} ${data.hoursOfOperation.monday.close.amorpm}`,
    mondayOpeningTime: `${data.hoursOfOperation.monday.open.openHour}:${data.hoursOfOperation.monday.open.openMinute} ${data.hoursOfOperation.monday.open.amorpm}`,
    openTuesday: data.hoursOfOperation.tuesday.areYouOpen,
    tuesdayClosingTime: `${data.hoursOfOperation.tuesday.close.closeHour}:${data.hoursOfOperation.tuesday.close.closeMinute} ${data.hoursOfOperation.tuesday.close.amorpm}`,
    tuesdayOpeningTime: `${data.hoursOfOperation.tuesday.open.openHour}:${data.hoursOfOperation.tuesday.open.openMinute} ${data.hoursOfOperation.tuesday.open.amorpm}`,
    openWednesday: data.hoursOfOperation.wednesday.areYouOpen,
    wednesdayClosingTime: `${data.hoursOfOperation.wednesday.close.closeHour}:${data.hoursOfOperation.wednesday.close.closeMinute} ${data.hoursOfOperation.wednesday.close.amorpm}`,
    wednesdayOpeningTime: `${data.hoursOfOperation.wednesday.open.openHour}:${data.hoursOfOperation.wednesday.open.openMinute} ${data.hoursOfOperation.wednesday.open.amorpm}`,
    openThursday: data.hoursOfOperation.thursday.areYouOpen,
    thursdayClosingTime: `${data.hoursOfOperation.thursday.close.closeHour}:${data.hoursOfOperation.thursday.close.closeMinute} ${data.hoursOfOperation.thursday.close.amorpm}`,
    thursdayOpeningTime: `${data.hoursOfOperation.thursday.open.openHour}:${data.hoursOfOperation.thursday.open.openMinute} ${data.hoursOfOperation.thursday.open.amorpm}`,
    openFriday: data.hoursOfOperation.friday.areYouOpen,
    fridayClosingTime: `${data.hoursOfOperation.friday.close.closeHour}:${data.hoursOfOperation.friday.close.closeMinute} ${data.hoursOfOperation.friday.close.amorpm}`,
    fridayOpeningTime: `${data.hoursOfOperation.friday.open.openHour}:${data.hoursOfOperation.friday.open.openMinute} ${data.hoursOfOperation.friday.open.amorpm}`,
    openSaturday: data.hoursOfOperation.saturday.areYouOpen,
    saturdayClosingTime: `${data.hoursOfOperation.saturday.close.closeHour}:${data.hoursOfOperation.saturday.close.closeMinute} ${data.hoursOfOperation.saturday.close.amorpm}`,
    saturdayOpeningTime: `${data.hoursOfOperation.saturday.open.openHour}:${data.hoursOfOperation.saturday.open.openMinute} ${data.hoursOfOperation.saturday.open.amorpm}`,
    reviewsForRatingValuePart1:
      forRatingValuePart1?.nodes.length &&
      forRatingValuePart1.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
    reviewsForRatingValuePart2:
      forRatingValuePart2?.nodes.length &&
      forRatingValuePart2.nodes.map((rating, i) => {
        return {
          index: i,
          rating: rating.rating,
        }
      }),
    reviewsQuery:
      query1?.nodes.length &&
      query1.nodes.map((review, i) => {
        return {
          index: i,
          id: review.id,
          sourceType: review.sourceType,
          reviewDate: review.reviewDate,
          rating: review.rating,
          reviewerFirstName: review.reviewer.firstName,
          reviewerLastName: review.reviewer.lastName,
          comments: review.comments,
          businessName: review.businessName,
        }
      }),
    offers: data.offersSchema?.map(offer => {
      return {
        offer: offer.offerSchema,
        url: offer.urlSchema,
      }
    }),
    awards: data.awardsSchema?.map(award => {
      return {
        award: award.awardSchema,
      }
    }),
    brands: data.brandsSchema?.map(brand => {
      return {
        brand: brand.brandSchema,
      }
    }),
    certifications: data.certificationsSchema?.map(certification => {
      return {
        certification: certification.certificationSchema,
      }
    }),
    languages: data.languagesSchema?.map(language => {
      return {
        language: language.languageSchema,
      }
    }),
    organizations: data.organizationsSchema?.map(organization => {
      return {
        organization: organization.organizationSchema,
      }
    }),
    socialProfilesDirectoryListings: data.socialMediaProfilesAndDirectoryListingsSchema?.map(
      item => {
        return {
          socialProfileDirectory: item.socialMediaProfileOrDirectoryListingSchema,
        }
      },
    ),
  }
}
export function SeoFunction(data) {
  const seoData = data.wpPage !== undefined ? data.wpPage : data.wpPost
  return {
    page:
      // eslint-disable-next-line no-nested-ternary
      data.wpPost !== undefined
        ? 'blogPost'
        : data.sitePage?.path === '/blog/'
          ? 'blogIndexPage'
          : null,
    favicon,
    title: seoData?.seo.title,
    metaDescription: seoData?.seo.metaDesc,
    ogTitle: seoData?.seo.opengraphTitle,
    ogDescription: seoData?.seo.opengraphDescription,
    ogUrl: seoData?.seo.opengraphUrl,
    ogSiteName: seoData?.seo.opengraphSiteName,
    ogType: seoData?.seo.opengraphType,
    ogImage: seoData?.seo.opengraphImage?.localFile.publicURL,
    canonicalUrl: seoData?.seo.canonical,
    twitterTitle: seoData?.seo.twitterTitle,
    twitterDescription: seoData?.seo.twitterDescription,
    twitterImage: seoData?.seo.twitterImage?.localFile.childImageSharp.gatsbyImageData,
  }
}
export function PageSpecificSchemaFunction(data) {
  const pageData = data.wpPage !== undefined ? data.wpPage : data.wpPost
  const pageSpecificSchemaData = usePageSpecificSchemaQuery().wp

  return {
    businessName: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.businessName,
    businessType: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.businessType,
    phoneNumber: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.phoneNumber,
    streetAddress: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.streetAddress,
    city: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.city,
    state: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.state,
    zipCode: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.zipCode,
    logo: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.logo.localFile.publicURL,
    blogPageUrl: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.blogPageUrl,
    aboutPageUrl: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.aboutPageUrl,
    contactPageUrl: pageSpecificSchemaData.nexvelSchemaMarkup.nexvelschema.contactPageUrl,
    siteUrl: data.site.siteMetadata.siteUrl,
    uri: pageData !== undefined ? pageData.uri : '/blog/',
    title: pageData !== undefined ? pageData.title : 'Blog',
    datePublished: pageData?.date,
    dateModified: pageData?.modified,
    image:
      pageData?.seo?.opengraphImage !== null
        ? // eslint-disable-next-line no-unsafe-optional-chaining
          data.site.siteMetadata.siteUrl + pageData?.seo?.opengraphImage.localFile.publicURL
        : '/blank.jpg',
    author: `${pageData?.author?.node.firstName} ${pageData?.author?.node.lastName}`,
    categories:
      pageData?.categories?.nodes.length &&
      pageData?.categories.nodes.map((category, i) => {
        return {
          index: i,
          name: category.name,
        }
      }),
    tags:
      pageData?.tags?.nodes.length &&
      pageData?.tags.nodes.map((tag, i) => {
        return {
          index: i,
          name: tag.name,
        }
      }),
    articleBody: pageData?.content,
    post: data.wpPost !== undefined,
    videos:
      pageData?.nexvelschemapagesposts?.videos?.length &&
      pageData?.nexvelschemapagesposts?.videos.map((video, i) => {
        return {
          index: i,
          title: video.title,
          url: video.url,
          description: video.description,
          thumbnailImage: video.thumbnailImage.localFile.publicURL,
          uploadDate: video.uploadDate,
        }
      }),
    questionsAndAnswers:
      pageData?.nexvelschemapagesposts?.questionsAndAnswers?.length &&
      pageData?.nexvelschemapagesposts?.questionsAndAnswers.map((qanda, i) => {
        return {
          index: i,
          question: qanda.question,
          answer: qanda.answer,
        }
      }),
    maps:
      pageData?.nexvelschemapagesposts?.maps?.length &&
      pageData?.nexvelschemapagesposts?.maps.map((map, i) => {
        return {
          index: i,
          url: map.mapUrl,
        }
      }),
    digitalDocuments:
      pageData?.nexvelschemapagesposts?.digitaldocuments?.length &&
      pageData?.nexvelschemapagesposts?.digitaldocuments.map((document, i) => {
        return {
          index: i,
          title: document.title,
        }
      }),
    images:
      pageData?.nexvelschemapagesposts?.images?.length &&
      pageData?.nexvelschemapagesposts?.images.map((item, i) => {
        return {
          index: i,
          image: item.image.localFile.publicURL,
          date: item.image.date,
          description: item.image.description,
          title: item.image.title,
        }
      }),
    areaServedGeneral: data.wp.nexvelSchemaMarkup?.nexvelschema.cities.map(city => {
      return {
        city: city.city,
      }
    }),
    areaServedPageSpecific: pageData?.nexvelschemapagesposts?.areaServedLandingPageSchema,
    serviceGeneral: data.wp.nexvelSchemaMarkup?.nexvelschema.services.map(service => {
      return {
        serviceName: service.service,
        relevantPage: service.linkToRelevantPageOnYourWebsite,
      }
    }),
    servicePageSpecific: {
      serviceName: pageData?.nexvelschemapagesposts?.serviceSchema.serviceNameSchema,
      relevantPage: pageData?.nexvelschemapagesposts?.serviceSchema.linkToRelevantServicePageSchema,
    },
    keywords: pageData?.nexvelschemapagesposts?.keywordsSchema?.map(item => {
      return {
        keyword: item.keywordSchema,
      }
    }),
    localAwards: pageData?.nexvelschemapagesposts?.localAwardsSchema?.map(item => {
      return {
        localAward: item.localAwardSchema,
      }
    }),
    latitude: pageData?.nexvelschemapagesposts?.latitudeSchema,
    longitude: pageData?.nexvelschemapagesposts?.longitudeSchema,
    relevantExternalPages: pageData?.nexvelschemapagesposts?.relevantExternalPagesSchema?.map(
      item => {
        return {
          relevantExternalPage: item.relevantExternalPageSchema,
        }
      },
    ),
  }
}

export function MultiStepFormFunction() {
  const { wp } = useMultiStepFormQuery()
  const form = wp.acfOptionsMultiStepForm.multiStepForm
  return {
    logo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
      alt: wp.acfOptionsCommonItems.commonItems.logoAltText,
      width: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.width,
      height: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.height,
    },
    stepTitles: form.stepTitles.map(stepTitle => {
      return {
        title: stepTitle.stepTitle,
      }
    }),
    van: {
      src: `/.netlify/images?url=${form.vanImage.sourceUrl}`,
      alt: form.vanImageAltText,
      width: form.vanImage.mediaDetails.width,
      height: form.vanImage.mediaDetails.height,
    },
    issues: form.issues.map(issue => {
      return {
        issue: issue.issue,
        subIssues: issue.subIssues,
      }
    }),
    moreInfo: form.moreInfo,
    photoText: form.photoText,
    maxUploadText: form.maxUploadText,
    ourPhoneNumber: wp.acfOptionsCommonItems.commonItems.phoneNumber,
    confirmationText: form.confirmationText,
    formSubmittedText: form.formSubmittedText,
    formAction: '/.netlify/functions/form-submission',
    bgColor: 'bg-white',
    borderColor: 'border-gray-dark',
    inputBorderColor: '[&>input]:border-gray',
    inputTextColor: '[&>input]:text-gray-dark [&>input]:mt-2',
    placeholderTextColor: '[&>input]:placeholder:text-gray-dark',
    continueButtonDisabled: 'text-gray fill-gray',
    continueButton: 'text-black fill-black',
    continueButtonBorder: 'border-black',
    continueButtonBorderDisabled: 'border-gray',
    stepsColor: 'text-white',
    stepsBgColor: 'bg-red',
    stepMarkerColorActive: 'bg-yellow',
    stepMarkerColor: 'bg-gray',
    selectArrow: 'bg-red',
    selectColor: 'text-gray-dark',
    paragraphColor: 'text-gray-dark',
    uploadBgColor: 'bg-white',
    arrowColor: 'bg-white',
    whatConvertsFormId: 135866,
    useCaptcha: true,
    captchaSiteKey: '6LdhlZ4qAAAAAE4azAcSsrX52AJG_nONwey8FFfF',
  }
}
export function BlogPostFunction(data) {
  const defaultImg = usePostHeroDefaultQuery()
  return {
    image: {
      src:
        data.wpPost.featuredImage !== null
          ? `/.netlify/images?url=${data.wpPost.featuredImage.node.sourceUrl}`
          : `/.netlify/images?url=${defaultImg.heroImageDefault.sourceUrl}`,
      width:
        data.wpPost.featuredImage !== null
          ? data.wpPost.featuredImage.node.mediaDetails.width
          : defaultImg.heroImageDefault.mediaDetails.width,
      height:
        data.wpPost.featuredImage !== null
          ? data.wpPost.featuredImage.node.mediaDetails.height
          : defaultImg.heroImageDefault.mediaDetails.height,
      alt:
        data.wpPost.featuredImage !== null && data.wpPost.postFeaturedImageAltText !== undefined
          ? data.wpPost.postFeaturedImageAltText.altText
          : defaultImg.heroImageDefaultAltText,
    },
    blogTitle: data.wpPost.title,
    content: data.wpPost.content,
    prevButtonTitle: 'BACK TO PREV',
    nextButtonTitle: 'NEXT POST',
    outerBgColor: 'bg-white',
  }
}
export function BlogOverviewFunction(data) {
  const defaultImg = usePostHeroDefaultQuery()
  return {
    blogList: data.allWpPost.nodes.map(post => {
      return {
        image:
          post.featuredImage !== null
            ? `/.netlify/images?url=${post.featuredImage.node.sourceUrl}`
            : `/.netlify/images?url=${defaultImg.heroImageDefault.sourceUrl}`,
        width:
          post.featuredImage !== null
            ? post.featuredImage.node.mediaDetails.width
            : defaultImg.heroImageDefault.mediaDetails.width,
        height:
          post.featuredImage !== null
            ? post.featuredImage.node.mediaDetails.height
            : defaultImg.heroImageDefault.mediaDetails.height,
        alt:
          post.featuredImage !== null && post.postFeaturedImageAltText !== undefined
            ? post.postFeaturedImageAltText.altText
            : defaultImg.heroImageDefaultAltText,
        createdAt: post.date,
        title: post.title,
        description: post.excerpt,
        link: `/blog/${post.categories.nodes[0].slug}${post.link}`,
      }
    }),
    blogCardStyles: {
      borderColor: 'border-gray',
      dateBgColor: 'bg-blue-dark',
      dateColor: 'text-white',
      titleColor: 'text-black',
    },
    postsPerPage: data.wp.allSettings.readingSettingsPostsPerPage,
    outerBgColor: 'bg-white',
    arrowColor: '#0C1F4A',
    paginationTextColor: 'text-blue-dark',
  }
}
export function InteriorCardsFunction() {
  const data = useWhatToExpectQuery()
  return {
    title: data.title,
    cardData: data.cards.map(card => {
      return {
        icon: `/.netlify/images?url=${card.icon.sourceUrl}`,
        title2: card.title,
        content: card.text,
      }
    }),
    iconFillColor: 'fill-white',
    title2Color: 'text-black',
    textColor: 'text-[#2F2F2F]',
    readMoreColor: 'text-orange',
    borderColor: 'border-gold',
    decoration: `/.netlify/images?url=https://wordpress-1332776-5079187.cloudwaysapps.com/wp-content/uploads/2024/12/decoration.svg`,
    cardBgColor: 'bg-white',
    cardHoverBgColor: 'hover:bg-gray-light',
  }
}
export function HeaderFunction(handleButton) {
  const {
    wp,
    mainMenuDropdownImages,
    mainMenu,
    topBarMenu,
    forRatingValuePart1,
    forRatingValuePart2,
    ctaBar,
  } = useHeaderQuery()

  function length(obj) {
    return Object.keys(obj).length
  }
  const averageRating = useMemo(() => {
    const reviewsRatings = {
      reviewsForRatingValuePart1:
        forRatingValuePart1?.nodes.length &&
        forRatingValuePart1.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
      reviewsForRatingValuePart2:
        forRatingValuePart2?.nodes.length &&
        forRatingValuePart2.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
    }
    const fiveFourThreeRatings = length(reviewsRatings.reviewsForRatingValuePart2)

    const totalRatings = length(reviewsRatings.reviewsForRatingValuePart1)

    const ratingsTotal = reviewsRatings.reviewsForRatingValuePart1
    const total = ratingsTotal.reduce((sum, item) => sum + item.rating, 0)

    let avgRating = total / fiveFourThreeRatings
    avgRating = avgRating.toFixed(1)

    return [avgRating, totalRatings]
  }, [forRatingValuePart1, forRatingValuePart2])
  function dropdownImg(a, b) {
    if (b.filter(e => e.associatedDropdown === a).length > 0) {
      const image2 = b.filter(e => e.associatedDropdown === a)

      return image2
    }
    return false
  }

  const menuImages = mainMenuDropdownImages.mainMenuImages.imageContainer.map(image => {
    return {
      src: image.image.sourceUrl,
      width: image.image.mediaDetails.width,
      height: image.image.mediaDetails.height,
      alt: image.altText,
      associatedDropdown: image.whichMenuItemIsThisAssociatedWith,
    }
  })
  return {
    topBgColor: 'bg-red',
    upperTextColor: 'text-white',
    upperTextHoverColor: 'hover:text-yellow',
    upperBorderColor: 'border-white',
    textColor: 'text-red lg:text-black hover:text-red',
    borderColor: 'border-black',
    rate: averageRating[0],
    starPrimary: '#ffffff',
    starSecondary: '#f9f9f9',
    reviewCnt: averageRating[1],
    reviewsLabel: wp.acfOptionsCommonItems.commonItems.reviewsLink.title,
    reviewLink: wp.acfOptionsCommonItems.commonItems.reviewsLink.url,
    topMenuData: topBarMenu.nodes.map(item => {
      return {
        title: item.label,
        link: item.path,
      }
    }),
    bgColor: 'bg-white',
    menuData: mainMenu.nodes.map(topLevel => {
      return {
        title: topLevel.label,
        menuImage: {
          src: dropdownImg(topLevel.label, menuImages)[0]?.src,
          width: dropdownImg(topLevel.label, menuImages)[0]?.width,
          height: dropdownImg(topLevel.label, menuImages)[0]?.height,
        },
        url: topLevel.path,
        altText: dropdownImg(topLevel.label, menuImages)[0]?.alt,
        subMenu: topLevel.childItems.nodes.length
          ? topLevel.childItems.nodes.map(secondLevel => {
              return {
                title: secondLevel.label,
                url: secondLevel.path,
                subMenu: [],
              }
            })
          : [],
      }
    }),
    menuLabel: 'menu',
    logo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
      width: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.width,
      height: wp.acfOptionsCommonItems.commonItems.logo.mediaDetails.height,
    },
    logoLink: '/',
    logoAltText: wp.acfOptionsCommonItems.commonItems.logoAltText,
    callButtonProps: {
      label: `${wp.acfOptionsCommonItems.commonItems.topCallText} ${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      notLink: false,
      fontSize:
        'uppercase text-[0.8rem] md:text-[1rem] lg:text-[0.65rem] xl:text-[0.875rem] 2xl:rfs:text-[1.25rem] lg:px-4 !py-4 xl:!py-5 xl:rfs:px-6 [&_svg]:!w-6 [&_svg]:!h-5 lg:[&_svg]:!w-0 lg:[&_svg]:!h-0 2xl:[&_svg]:!w-8 2xl:[&_svg]:!h-8',
      textColor: 'text-black lg:text-red',
      borderColor: 'border-yellow lg:border-white',
      bgColor: 'bg-yellow lg:bg-white',
      hoverBgColor: 'lg:hover:bg-tan',
      hoverBorderColor: 'lg:hover:border-tan',
      hoverTextColor: 'lg:hover:text-red',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      icon: true,
      phoneOrCalender: 'phone',
    },
    requestButtonProps: {
      label: wp.acfOptionsCommonItems.commonItems.topButtonText,
      notLink: true,
      borderColor: 'border-red lg:border-yellow',
      fontSize:
        'uppercase text-[0.8rem] md:text-[1rem] lg:text-[0.65rem] xl:text-[0.875rem] 2xl:rfs:text-[1.25rem] lg:px-4 !py-4 xl:!py-5 xl:rfs:px-16',
      textColor: 'text-white lg:text-black',
      bgColor: 'bg-red lg:bg-yellow',
      hoverBgColor: 'lg:hover:bg-orange',
      hoverBorderColor: 'lg:hover:border-orange',
      hoverTextColor: 'lg:hover:text-white',
      onClick: handleButton,
      icon: false,
    },
    desktopPhoneColor: '#E2261D',
    mobilePhoneColor: '#000000',
    megaMenuBgColor: 'bg-red-dark/[96%]',
    megaSubmenuBgColor: '',
    menuImageAltText: '',
    megaMenuTextColor: 'text-white',
    megaMenuHoverBgColor: 'hover:bg-red',
    megaMenuBorderColor: 'border-white/[20%]',
    megaMenuFontWeight: 'font-black',
    specialOfferButtonProps: {
      notLink: false,
      bgColor: 'bg-white',
      textColor: 'text-red',
      hoverBgColor: 'hover:bg-gray',
      hoverTextColor: 'hover:text-red',
      fontSize: 'rfs:text-[1.3125rem]',
      padding: 'py-4 rfs:px-20',
      buttonText: wp.acfOptionsCommonItems.commonItems.menuLink.title,
      href: wp.acfOptionsCommonItems.commonItems.menuLink.url,
    },
    scheduleButtonProps: {
      notLink: true,
      bgColor: 'bg-red',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-orange',
      hoverTextColor: 'hover:text-white',
      fontSize: 'rfs:text-[1.3125rem]',
      padding: 'py-4 rfs:px-20',
      buttonText: wp.acfOptionsCommonItems.commonItems.topButtonText,
      onClick: handleButton,
    },
    onRequestServiceClick: handleButton,
    phoneNumber: wp.acfOptionsCommonItems.commonItems.phoneNumber,
    mobileMenuBgColor: 'bg-[#e4e4e4]',
    mobileMenuBorderColor: 'border-[#959595]',
    mobileMenuTextColor: 'text-black-light3',
    mobileMenuHeaderBgColor: 'bg-white',
    closeTextColor: 'text-black',
    breadCrumbHomeLabel: 'Home',
    breadCrumbDivider: '/',
    breadCrumbTextColor: 'text-black-light3',
    mobileActive: 'active:bg-yellow',
    mobileSubMenuBgColor: 'bg-[#e4e4e4]',
    mobileSubMenuFontWeight: 'font-black',
    mobileMenuCallButtonProps: {
      notLink: false,
      bgColor: 'bg-white',
      textColor: 'text-red',
      hoverBgColor: '',
      hoverTextColor: '',
      fontSize: 'text-[1.26rem]',
      buttonText: wp.acfOptionsCommonItems.commonItems.phoneNumber,
      hasPhone: true,
      phoneFill: '[&_path]:fill-red',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
    },
    mobileMenuRequestButtonProps: {
      notLink: true,
      bgColor: 'bg-red',
      textColor: 'text-white',
      hoverBgColor: '',
      hoverTextColor: '',
      fontSize: 'text-[1.26rem]',
      buttonText: wp.acfOptionsCommonItems.commonItems.topButtonText,
      onClick: handleButton,
    },
    topNavMobile: ctaBar.ctaBar.ctaBar.ctas.map(item => {
      return {
        title: item.link.title,
        url: item.link.url,
        icon: item.icon.sourceUrl,
      }
    }),
    topMenuBg1: 'bg-red',
    topMenuBg2: 'bg-red-dark',
    activeBorderColor: 'border-orange',
    mobileTopTextColor: '!text-white',
    arrowFill: '[&_path]:stroke-black',
    button2BgColor: 'bg-yellow hover:bg-orange',
    mobileReviewsColor: 'text-red',
    reviewsLabelMobile: wp.acfOptionsCommonItems.commonItems.reviewTextMobile,
    starPrimaryMobile: '#FC9E15',
  }
}
export function InteriorHeroFunction(data, handleButton) {
  const interior = useInteriorHeroQuery()
  return {
    bgImg:
      data.wpPage.hero.hero.backgroundImageDesktop !== null
        ? `/.netlify/images?url=${data.wpPage.hero.hero.backgroundImageDesktop.sourceUrl}`
        : `/.netlify/images?url=${interior.interiorPage.heroImageDefault.sourceUrl}`,
    titleColor: 'text-white',
    title: data.wpPage.hero.hero.title,
    subtitle: data.wpPage.hero.hero.text,
    callButtonProps: {
      label: `${interior.topCallText} ${interior.phoneNumber}`,
      notLink: false,
      fontSize: 'uppercase text-[1rem] lg:rfs:text-[1.25rem] rfs:!px-10 !py-4',
      textColor: 'text-red',
      borderColor: 'border-white',
      bgColor: 'bg-white',
      hoverBgColor: 'hover:bg-tan',
      hoverBorderColor: 'hover:border-tan',
      hoverTextColor: 'lhover:text-red',
      href: `tel:${interior.phoneNumber}`,
      icon: true,
      iconColor: '#E2261D',
      phoneOrCalender: 'phone',
    },
    scheduleButtonProps: {
      label: interior.topButtonText,
      notLink: true,
      borderColor: 'border-red',
      fontSize: 'uppercase text-[1rem] lg:rfs:text-[1.25rem] rfs:!px-20',
      textColor: 'text-white',
      bgColor: 'bg-red',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-white',
      onClick: handleButton,
      icon: false,
    },
    gradientColor: 'bg-gradient-to-r from-black via-black/[70%]',
  }
}
export function BreadcrumbFunction(data) {
  return {
    breadcrumbs: data.wpPage.breadcrumbs.breadcrumbs.breadcrumbs,
  }
}
export function ContentSection1Function(data) {
  return {
    contentSection: data.wpPage.contentSection1.contentSection1.contentSection1,
  }
}
export function ContentSection2Function(data) {
  const accordion = useInteriorAccordionQuery()

  return {
    title: data.wpPage.contentSection2.contentSection2.title,
    content: data.wpPage.contentSection2.contentSection2.contentSection2,
    img: {
      src:
        data.wpPage.contentSection2.contentSection2.image !== null
          ? `/.netlify/images?url=${data.wpPage.contentSection2.contentSection2.image.sourceUrl}`
          : `/.netlify/images?url=${accordion.accordionDefaultImage.sourceUrl}`,
      width:
        data.wpPage.contentSection2.contentSection2.image !== null
          ? data.wpPage.contentSection2.contentSection2.image.mediaDetails.width
          : accordion.accordionDefaultImage.mediaDetails.width,
      height:
        data.wpPage.contentSection2.contentSection2.image !== null
          ? data.wpPage.contentSection2.contentSection2.image.mediaDetails.height
          : accordion.accordionDefaultImage.mediaDetails.height,
    },
    imgAltText:
      data.wpPage.contentSection2.contentSection2.imageAltText !== null
        ? data.wpPage.contentSection2.contentSection2.imageAltText
        : accordion.accordionDefaultImageAltText,
    accordions: data.wpPage.contentSection2.contentSection2.accordion.map(item => {
      return {
        title: item.label,
        content: item.content,
      }
    }),
    icon: accordion.accordionIcon.sourceUrl,
    arrowIcon: accordion.accordionArrow.sourceUrl,
    textColor: 'text-black',
    numberColor: 'text-white',
    bgColor: 'bg-[#F3F3F5]',
    bgColor2: 'bg-white',
    borderColor: 'border-[#F89422]',
  }
}
export function Callout2Function(handleButton) {
  const data = useCallout2Query()
  return {
    bgImg: `/.netlify/images?url=${data.backgroundImageDesktop.sourceUrl}`,
    bgImgMobile: `/.netlify/images?url=${data.backgroundImageMobile.sourceUrl}`,
    bgColor: 'bg-gradient-to-r from-[#9E241F] to-red',
    title: data.title,
    subtitle: data.subtitle,
    requestButtonProps: {
      notLink: true,
      bgColor: 'bg-yellow',
      textColor: 'text-black',
      hoverBgColor: 'hover:bg-orange',
      hoverTextColor: 'hover:text-black',
      fontSize: 'text-[1.125rem] md:rfs:text-[1.3125rem]',
      buttonText: data.buttonText,
      padding: 'py-4 rfs:px-8',
      hasArrow: true,
      arrowFill: '[&_path]:fill-black',
      onClick: handleButton,
    },
    learnMoreButtonProps: {
      bgColor: 'bg-white',
      textColor: 'text-red',
      hoverBgColor: 'hover:bg-tan',
      hoverTextColor: 'hover:text-red',
      fontSize: 'text-[1.125rem] md:rfs:text-[1.3125rem]',
      buttonText: data.link.title,
      padding: 'py-4 rfs:px-8',
      hasArrow: true,
      arrowFill: '[&_path]:fill-red',
      href: data.link.url,
    },
    textColor: 'text-white',
  }
}
export function ContentSection3Function(data) {
  return {
    contentSection: data.wpPage.contentSection3.contentSection3.contentSection3,
  }
}
export function RelatedLinksFunction(data) {
  return {
    contentSection: data.wpPage.contentSection3.contentSection3.relatedLinks,
  }
}
export function XpinsFunction(data) {
  const { wp, site } = useXPinsQuery()

  function generateRandomCoordinates(center, radius, cards) {
    // Convert radius from miles to degrees (approximate)
    // 1 degree of latitude = ~69 miles
    const radiusInDegrees = radius / 69

    const coordinates = []

    // Generate coordinates for each card
    if (center?.latitude && center?.longitude) {
      // Get the cards array safely
      const cardsToUse = cards || []

      cardsToUse.forEach(() => {
        // Generate random angle and distance
        const angle = Math.random() * 2 * Math.PI
        const distance = Math.random() * radiusInDegrees

        // Calculate offset
        const latOffset = distance * Math.cos(angle)
        const lngOffset = (distance * Math.sin(angle)) / Math.cos((center.latitude * Math.PI) / 180)

        // Add new coordinate
        coordinates.push({
          latitude: parseFloat(center.latitude) + parseFloat(latOffset),
          longitude: parseFloat(center.longitude) + parseFloat(lngOffset),
        })
      })
    }

    return coordinates
  }

  // Get center coordinates with fallback
  const mapCenter = {
    latitude:
      data?.wpPage?.xPins?.xPins?.mapCenter?.latitude ||
      wp.acfOptionsCommonItems.commonItems.xPins.defaultLatitude,
    longitude:
      data?.wpPage?.xPins?.xPins?.mapCenter?.longitude ||
      wp.acfOptionsCommonItems.commonItems.xPins.defaultLongitude,
  }

  // Get radius with fallback
  const radius = data?.wpPage?.xPins?.xPins?.radius || 2

  // Get cards with fallback
  const cards =
    data?.wpPage?.xPins?.xPins?.cards || wp.acfOptionsCommonItems.commonItems.xPins.defaultCardData

  // Generate coordinates
  const coordinates = generateRandomCoordinates(mapCenter, radius, cards)

  return {
    toptitleColor: 'text-orange',
    toptitle: wp.acfOptionsCommonItems.commonItems.xPins.serviceAreasTitle,
    title: wp.acfOptionsCommonItems.commonItems.xPins.serviceAreasSubtitle,
    titleColor: 'text-black-light5',
    serviceAreasBgColor: 'bg-gradient-to-tr from-red to-[#9E241F]',
    serviceAreasTitle: wp.acfOptionsCommonItems.commonItems.xPins.accordionTitle,
    serviceAreasTextColor: 'text-white',
    services: wp.acfOptionsCommonItems.commonItems.xPins.services.map(service => {
      return {
        label: service.serviceTitle,
        counties: service.counties.map(county => {
          return {
            county: county.county,
            towns: county.towns,
          }
        }),
      }
    }),
    locationIcon: wp.acfOptionsCommonItems.commonItems.xPins.locationIcon.sourceUrl,
    carrotIcon: wp.acfOptionsCommonItems.commonItems.xPins.carrotIcon.sourceUrl,
    borderColor: 'border-gray/[70%]',
    serviceBorderColor: 'border-yellow',
    mapApiKey: process.env.GATSBY_MAP_API_KEY,
    mapCenter: {
      lat: parseFloat(
        data.wpPage.xPins.xPins.mapCenter.latitude !== null
          ? data.wpPage.xPins.xPins.mapCenter.latitude
          : wp.acfOptionsCommonItems.commonItems.xPins.defaultLatitude,
      ),
      lng: parseFloat(
        data.wpPage.xPins.xPins.mapCenter.longitude !== null
          ? data.wpPage.xPins.xPins.mapCenter.longitude
          : wp.acfOptionsCommonItems.commonItems.xPins.defaultLongitude,
      ),
    },
    mapZoom: 11,
    locationData:
      data.wpPage.xPins.xPins.cards !== null
        ? data.wpPage.xPins.xPins.cards.map((card, cardIndex) => {
            return {
              name: wp.acfOptionsCommonItems.commonItems.xPins.cardsAuthor,
              date: card.date,
              content: card.description,
              image:
                card.image !== null
                  ? `/.netlify/images?url=${card.image.sourceUrl}`
                  : `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.xPins.defaultCardImage.sourceUrl}`,
              lat: parseFloat(coordinates[cardIndex].latitude),
              lng: parseFloat(coordinates[cardIndex].longitude),
            }
          })
        : wp.acfOptionsCommonItems.commonItems.xPins.defaultCardData.map((card, cardIndex) => {
            return {
              name: wp.acfOptionsCommonItems.commonItems.xPins.cardsAuthor,
              date: card.date,
              content: card.content,
              image:
                card.image !== null
                  ? `/.netlify/images?url=${card.image.sourceUrl}`
                  : `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.xPins.defaultCardImage.sourceUrl}`,
              lat: parseFloat(coordinates[cardIndex].latitude),
              lng: parseFloat(coordinates[cardIndex].longitude),
            }
          }),
    xpinIcon: wp.acfOptionsCommonItems.commonItems.xPins.pinIcon.sourceUrl,
    xPinTitle: wp.acfOptionsCommonItems.commonItems.xPins.cardsTitle,
    cardBgColor: 'bg-white',
    cardBorderColor: 'border-red',
    cardLabelBgColor: 'bg-red',
    cardLabelTextColor: 'text-white',
    dateColor: 'text-gray-darkest',
    bgColor: 'bg-tan',
    topBgColor: 'bg-white',
    schemaData: {
      type: wp.nexvelSchemaMarkup.nexvelschema.businessType,
      name: wp.nexvelSchemaMarkup.nexvelschema.businessName,
      address: wp.nexvelSchemaMarkup.nexvelschema.streetAddress,
      city: wp.nexvelSchemaMarkup.nexvelschema.city,
      state: wp.nexvelSchemaMarkup.nexvelschema.state,
      zip: wp.nexvelSchemaMarkup.nexvelschema.zipCode,
      url: site.siteMetadata.siteUrl,
      sameAs: wp.acfOptionsCommonItems.commonItems.footer.socialMedia.map(social => {
        return {
          url: social.socialMediaLink.url,
        }
      }),
    },
  }
}

export function InteriorCouponFunction(handleButton, data) {
  const fallback = useRebatesQuery()
  return {
    title:
      data.wpPage.interiorCoupon.interiorCoupon.title !== null
        ? data.wpPage.interiorCoupon.interiorCoupon.title
        : fallback.rebates[0].title,
    logo: {
      src: `/.netlify/images?url=${fallback.logo.sourceUrl}&q=100`,
      width: fallback.logo.mediaDetails.width,
      height: fallback.logo.mediaDetails.height,
      alt: 'logo',
    },
    bgColor: 'bg-orange',
    subtitleColor: 'text-yellow',
    textColor: 'text-white',
    subtitle:
      data.wpPage.interiorCoupon.interiorCoupon.title !== null
        ? data.wpPage.interiorCoupon.interiorCoupon.subtitle
        : fallback.rebates[0].subtitle,
    button: {
      text: fallback.buttonText,
      color: 'text-orange',
      bgColor: 'bg-white',
      onClick: handleButton,
    },
    text:
      data.wpPage.interiorCoupon.interiorCoupon.title !== null
        ? data.wpPage.interiorCoupon.interiorCoupon.text
        : fallback.rebates[0].text,
    buttonColor: 'text-white',
  }
}
export function SidebarReviewsFunction() {
  const { reviews } = useTestimonialsSectionQuery()
  return {
    reviewsCardProps: {
      bgColor: 'bg-white',
      starPrimary: '#D6612D',
      starSecondary: '#f2f2f2',
      rate: reviews.nodes[0].rating,
      icon: `/.netlify/images?url=https://wordpress-1332776-5079187.cloudwaysapps.com/wp-content/uploads/2024/12/google-icon.svg`,
      content: reviews.nodes[0].comments,
      textColor: 'text-black',
      fullName: `${reviews.nodes[0].reviewer.firstName} ${reviews.nodes[0].reviewer.lastName}`,
      nameColor: 'text-[#242424]',
    },
    buttonProps: {
      bgColor: `bg-yellow`,
      textColor: 'text-black',
      hoverBgColor: 'hover:bg-red',
      hoverTextColor: 'hover:text-white',
      fontSize: 'text-[1.3125rem] lg:rfs:text-[1.3125rem]',
      buttonText: 'read all reviews',
      href: '/about-us/reviews/',
    },
  }
}
export function Callout1Function(handleButton) {
  const callout = useCallout1Query()

  return {
    bgImg: `/.netlify/images?url=${callout.backgroundImageDesktop.sourceUrl}`,
    bgImgMobile: `/.netlify/images?url=${callout.backgroundImageMobile.sourceUrl}`,
    title: callout.title,
    subtitle: callout.subtitle,
    buttonProps: {
      notLink: true,
      bgColor: 'bg-yellow',
      textColor: 'text-black [&_path]:fill-black',
      hoverBgColor: 'hover:bg-orange',
      hoverTextColor: 'hover:text-black',
      fontSize: 'text-[1.125rem] md:rfs:text-[1.3125rem] 3xl:rfs:text-[1rem]',
      buttonText: callout.buttonText,
      hasArrow: true,
      onClick: handleButton,
    },
    textColor: 'text-white',
    vanImage: {
      src: `/.netlify/images?url=${callout.van.sourceUrl}`,
      width: callout.van.mediaDetails.width,
      height: callout.van.mediaDetails.height,
    },
    vanImageAltText: callout.vanAltText,
    bgGradient: 'bg-gradient-to-b lg:bg-gradient-to-r from-red from-40% lg:to-[#4F180E]/50 to-55%',
  }
}
export function FaqFunction(data) {
  return {
    bgColor: 'bg-gray-lighter',
    bgColor2: 'bg-white',
    topTitleColor: `text-red`,
    topTitle: data.wpPage.faqs.faqs.title,
    titleColor: 'text-black',
    title: data.wpPage.faqs.faqs.subtitle,
    faqs: data.wpPage.nexvelschemapagesposts.questionsAndAnswers.map(faq => {
      return {
        question: faq.question,
        answer: faq.answer,
      }
    }),
    borderColor: 'border-[#C8C8C8]/[70%]',
    qaColor1: `text-red`,
    questionColor: 'text-black',
    plusMinusColor: 'text-[#6B6B6B]',
    answerBgColor: `bg-red [&_li]:list-disc`,
    answerTextColor: 'text-white',
  }
}
export function ContactFunction() {
  const data = useContactQuery()
  return {
    title: data.title,
    subtitle: data.subtitle,
    bgImg: `/.netlify/images?url=${data.backgroundImageDesktop.sourceUrl}`,
    bgImgMobile: '',
    textColor: 'text-white',
    subtitleColor: 'text-yellow',
    bgColor: 'bg-red-dark',
    form: {
      textColor: '[&>input]:placeholder:text-white placeholder:text-white text-white',
      borderColor: '[&>input]:border-white',
      actionUrl: '/thank-you/',
      submitButton: {
        rounded: false,
        hasArrow: false,
        notLink: true,
        primaryFont: true,
        label: data.buttonText,
        bgColor: 'bg-white',
        fontSize: 'text-[1.125rem] sm:text-[1.5rem] md:text-[2rem] lg:text-[1.75rem]',
        textColor: 'text-red',
        hoverBg: 'hover:bg-red',
        hoverTextColor: 'hover:text-white',
      },
    },
  }
}
export function FooterFunction() {
  const { wp, footerMenu1, footerMenu2 } = useFooterQuery()
  const { footer } = wp.acfOptionsCommonItems.commonItems
  return {
    bgColor: 'bg-black',
    logo: {
      src: `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.logo.sourceUrl}`,
      width: 147,
      height: 153,
      alt: wp.acfOptionsCommonItems.commonItems.logoAltText,
    },
    socialMedia: footer.socialMedia.map(social => {
      return {
        logo: <ReactSVG src={`/.netlify/images?url=${social.socialMediaIcon.sourceUrl}`} />,
        link: social.socialMediaLink.url,
      }
    }),
    menu1Title: footerMenu1.nodes[0].menu.node.name,
    menu1: footerMenu1.nodes.map(menuItem => {
      return {
        label: menuItem.label,
        path: menuItem.path,
      }
    }),
    menu2Title: footerMenu2.nodes[0].menu.node.name,
    menu2: footerMenu2.nodes.map(menuItem => {
      return {
        label: menuItem.label,
        path: menuItem.path,
      }
    }),
    contactLabel: footer.contact.label,
    locations: footer.contact.addresses.map((address, i) => {
      return {
        address: address.address,
      }
    }),
    phoneNumber: wp.acfOptionsCommonItems.commonItems.phoneNumber,
    officeHoursLabel: footer.officeHours.label,
    officeHours: footer.officeHours.text,
    copyright: footer.copyright,
    nexvel: {
      logo: <ReactSVG src={`/.netlify/images?url=${footer.nexvelLogo.sourceUrl}`} />,
      link: footer.nexvelLink.url,
    },
    headingColor: 'text-white',
    textColor: 'text-white',
    borderTopColor: 'border-black',
    triangleColor: '[&_path]:fill-red',
  }
}

export function HomeHeroFunction(handleButton) {
  const { wp, wpPage } = useHeroHomeQuery()
  return {
    bgImg: `/.netlify/images?url=${wpPage.hero.hero.backgroundImageDesktop.sourceUrl}`,
    bgImgMobile: `/.netlify/images?url=${wpPage.hero.hero.backgroundImageMobile.sourceUrl}`,
    bgImgBlur: `/.netlify/images?url=${wpPage.hero.hero.backgroundImageDesktopPlaceholder.sourceUrl}`,
    bgImgMobileBlur: `/.netlify/images?url=${wpPage.hero.hero.backgroundImageMobilePlaceholder.sourceUrl}`,
    titleColor: 'text-white',
    title: wpPage.hero.hero.title,
    subtitle: wpPage.hero.hero.text,
    callButtonProps: {
      label: `${wp.acfOptionsCommonItems.commonItems.topCallText} ${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      notLink: false,
      fontSize: 'uppercase text-[1rem] lg:rfs:text-[1.25rem] rfs:!px-10 !py-4',
      textColor: 'text-red',
      borderColor: 'border-white',
      bgColor: 'bg-white',
      hoverBgColor: 'hover:bg-tan',
      hoverBorderColor: 'hover:border-tan',
      hoverTextColor: 'lhover:text-red',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      icon: true,
      iconColor: '#E2261D',
      phoneOrCalender: 'phone',
    },
    scheduleButtonProps: {
      label: wp.acfOptionsCommonItems.commonItems.topButtonText,
      notLink: true,
      borderColor: 'border-red',
      fontSize: 'uppercase text-[1rem] lg:rfs:text-[1.25rem] rfs:!px-20',
      textColor: 'text-white',
      bgColor: 'bg-red',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-white',
      onClick: handleButton,
      icon: false,
    },
    gradientColor: 'bg-gradient-to-b lg:bg-gradient-to-r from-black via-black/[70%]',
  }
}

export function AwardsFunction() {
  const data = useAwardsQuery()
  return {
    titleColor: 'text-black',
    awardData: data.awards.map(award => {
      return {
        image: {
          src: `/.netlify/images?url=${award.logo.sourceUrl}`,
          width: award.logo.mediaDetails.width,
          height: award.logo.mediaDetails.height,
        },
        altText: award.altText,
        link: award.link.url,
      }
    }),
    gradientColor: 'lg:bg-gradient-to-r from-white',
    hoverBorder: 'lg:hover:border-4 border-yellow',
  }
}

export function OurServicesFunction(handleButton) {
  const { wpPage, wp } = useOurServicesQuery()
  return {
    bgImage: wpPage.ourServices.ourServices.backgroundImageDesktop.sourceUrl,
    bgColor: 'bg-red-dark',
    textColor: 'text-white',
    titleColor: 'text-white',
    toptitleColor: 'text-yellow',
    toptitle: wpPage.ourServices.ourServices.title,
    borderColor: 'border-gray/[20%]',
    title: wpPage.ourServices.ourServices.subtitle,
    serviceCategories: wpPage.ourServices.ourServices.services.map(service => {
      return {
        icon: service.icon.sourceUrl,
        image: {
          src: `/.netlify/images?url=${service.image.sourceUrl}`,
          alt: service.altText,
          width: service.image.mediaDetails.width,
          height: service.image.mediaDetails.height,
        },
        label: service.label,
        title: service.title,
        text: service.text,
        link: service.link !== null ? service.link.url : undefined,
        linkText: service.link !== null ? service.link.title : '',
        services:
          service.tabServices !== null
            ? service.tabServices.map(tab => {
                return {
                  icon: tab.icon.sourceUrl,
                  label: tab.label,
                  title: tab.label,
                  text: tab.content,
                  link: {
                    url: tab.link.url,
                    text: tab.link.title,
                  },
                }
              })
            : [],
      }
    }),
    activeCategoryIconColor: '[&_path]:fill-yellow',
    buttonHover: '[&_path]:hover:fill-yellow hover:text-yellow',
    buttonActive: 'border-transparent [&_path]:fill-yellow text-yellow',
    callButton: {
      rounded: false,
      twoLabels: true,
      label: wp.acfOptionsCommonItems.commonItems.topCallText,
      label2: wp.acfOptionsCommonItems.commonItems.phoneNumber,
      notLink: false,
      fontWeight: 'font-bold',
      fontSize: 'text-base rfs:lg:text-[1.5rem]',
      fontFamily: 'font-primary',
      textColor: 'text-red',
      label2Color: 'text-red',
      borderColor: 'border-white',
      bgColor: 'bg-white',
      hoverBgColor: 'hover:bg-tan',
      hoverBorderColor: 'hover:border-tan',
      hoverTextColor: 'hover:text-red',
      padding: 'rfs:py-5',
      href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
      phoneIcon: true,
      iconColor: '#E2261D',
    },
    estimateButton: {
      rounded: false,
      notLink: true,
      label: wp.acfOptionsCommonItems.commonItems.topButtonText,
      borderColor: 'border-yellow',
      fontWeight: 'font-bold',
      fontSize: 'text-base rfs:lg:text-[1.5rem]',
      fontFamily: 'font-primary',
      bgColor: 'bg-yellow',
      textColor: 'text-black',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-white',
      padding: 'rfs:py-5',
      onClick: handleButton,
    },
    learnMoreButtonColor:
      'border-white hover:border-yellow bg-transparent text-white font-bold hover:text-yellow',
    activeBorderColor: 'border-yellow lg:border-white',
    buttonActiveDesktop: '[&_path]:fill-yellow text-yellow',
    selectEven: 'border-r-yellow',
    selectOdd: 'border-l-yellow',
    arrowActiveColor: '[&_path]:fill-yellow',
  }
}

export function MeetOurTeamFunction() {
  const data = useMeetOurTeamQuery()
  return {
    toptitleColor: 'text-red',
    toptitle: data.title,
    titleColor: 'text-black',
    title: data.subtitle,
    teamData: data.teamMembers.map(member => {
      return {
        image: `/.netlify/images?url=${member.image.sourceUrl}`,
        name: member.name,
        description: member.description,
      }
    }),
  }
}
export function WelcomeFunction() {
  const data = useWelcomeQuery()
  return {
    titleColor: 'text-orange',
    title: data.title,
    subtitle: data.subtitle,
    text: data.text,
    button: {
      rounded: false,
      notLink: false,
      label: data.link.title,
      borderColor: 'border-white',
      fontWeight: 'font-bold',
      fontSize: 'text-base',
      fontFamily: 'font-primary',
      bgColor: 'bg-yellow',
      textColor: 'text-black',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-black',
      padding: 'rfs:py-5',
      href: data.link.url,
    },
    image: {
      src: `/.netlify/images?url=${data.image.sourceUrl}`,
      width: data.image.mediaDetails.width,
      height: data.image.mediaDetails.height,
      alt: data.altText,
    },
    playButton: `/.netlify/images?url=${data.playButton.sourceUrl}`,
    vimeoId: data.vimeoVideoId,
    squareColor: 'bg-yellow',
    van: {
      src: `/.netlify/images?url=${data.vanImageDesktop.sourceUrl}`,
      width: data.vanImageDesktop.mediaDetails.width,
      height: data.vanImageDesktop.mediaDetails.height,
      alt: data.vanAltText,
    },
    videoCTAColor: 'text-white',
    overlayColor: 'bg-black/40',
  }
}
export function RebatesFunction(handleButton) {
  const data = useRebatesQuery()
  return {
    bgColor: 'bg-gray-light2',
    topTitleColor: 'text-orange',
    titleColor: 'text-black',
    topTitle: data.title,
    title: data.subtitle,
    rebateData: data.rebates.map((rebate, i) => {
      return {
        topBgColor: 'bg-white',
        title: rebate.title,
        logo: {
          src: `/.netlify/images?url=${data.logo.sourceUrl}`,
          width: data.logo.mediaDetails.width,
          height: data.logo.mediaDetails.height,
          alt: 'logo',
        },
        bgColor: i % 2 === 0 ? 'bg-orange' : 'bg-red',
        subtitleColor: 'text-yellow',
        textColor: 'text-white',
        subtitle: rebate.subtitle,
        button: {
          text: data.buttonText,
          color: 'text-red',
          bgColor: 'bg-white',
          onClick: handleButton,
        },
        text: rebate.text,
        buttonColor: 'text-white',
      }
    }),
    requestButtonProps: {
      rounded: false,
      notLink: false,
      href: data.link.url,
      label: data.link.title,
      borderColor: 'border-yellow',
      fontWeight: 'font-black',
      fontSize: 'text-lg lg:text-2xl',
      fontFamily: 'font-primary',
      bgColor: 'bg-yellow',
      textColor: 'text-black',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-black',
      padding: 'py-5 px-0',
    },
  }
}
export function TestimonialsFunction() {
  const { wpPage, forRatingValuePart1, forRatingValuePart2, reviews } =
    useTestimonialsSectionQuery()
  const testimonials = wpPage.testimonialsSection.testimonialsSection.testimonials
  const videos = wpPage.testimonialsSection.testimonialsSection.videosSection
  function length(obj) {
    return Object.keys(obj).length
  }
  const averageRating = useMemo(() => {
    const reviewsRatings = {
      reviewsForRatingValuePart1:
        forRatingValuePart1?.nodes.length &&
        forRatingValuePart1.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
      reviewsForRatingValuePart2:
        forRatingValuePart2?.nodes.length &&
        forRatingValuePart2.nodes.map((rating, i) => {
          return {
            index: i,
            rating: rating.rating,
          }
        }),
    }
    const fiveFourThreeRatings = length(reviewsRatings.reviewsForRatingValuePart2)

    const totalRatings = length(reviewsRatings.reviewsForRatingValuePart1)

    const ratingsTotal = reviewsRatings.reviewsForRatingValuePart1
    const total = ratingsTotal.reduce((sum, item) => sum + item.rating, 0)

    let avgRating = total / fiveFourThreeRatings
    avgRating = avgRating.toFixed(1)

    return [avgRating, totalRatings]
  }, [forRatingValuePart1, forRatingValuePart2])
  return {
    testimonialsProps: {
      title: testimonials.title,
      subtitle: testimonials.subtitle,
      subtitleColor: 'text-orange',
      reviewsBadge: {
        reviewsWidgetText: testimonials.reviewWidgetText,
        rate: averageRating[0],
        reviewCnt: averageRating[1],
        innerBorderColor: 'before:border-white',
        bgColor: 'bg-red',
        textColor: 'text-white',
        instance: 1,
        emptyStarColor: 'fill-gray-lighter',
        starColor: 'fill-white',
        reviewsRatings: {
          reviewsForRatingValuePart1:
            forRatingValuePart1?.nodes.length &&
            forRatingValuePart1.nodes.map((rating, i) => {
              return {
                index: i,
                rating: rating.rating,
              }
            }),
          reviewsForRatingValuePart2:
            forRatingValuePart2?.nodes.length &&
            forRatingValuePart2.nodes.map((rating, i) => {
              return {
                index: i,
                rating: rating.rating,
              }
            }),
        },
      },
      review: {
        firstName: reviews.nodes[0].reviewer.firstName,
        lastName: reviews.nodes[0].reviewer.lastName,
        logoImage:
          reviews.nodes[0].sourceType === 'Our Website'
            ? 'LocalReviews.Buzz'
            : reviews.nodes[0].sourceType,
        content: reviews.nodes[0].comments,
      },
      google: {
        logo: `/.netlify/images?url=${testimonials.reviewIcons.googleIcon.sourceUrl}`,
        src: `/.netlify/images?url=${testimonials.reviewIcons.googleIcon.sourceUrl}`,
        width: testimonials.reviewIcons.googleIcon.mediaDetails.width,
        height: testimonials.reviewIcons.googleIcon.mediaDetails.height,
      },
      localReviews: {
        logo: `/.netlify/images?url=${testimonials.reviewIcons.localreviewsbuzzIcon.sourceUrl}`,
        src: `/.netlify/images?url=${testimonials.reviewIcons.localreviewsbuzzIcon.sourceUrl}`,
        width: testimonials.reviewIcons.localreviewsbuzzIcon.mediaDetails.width,
        height: testimonials.reviewIcons.localreviewsbuzzIcon.mediaDetails.height,
      },
      link: {
        title: testimonials.link.title,
        url: testimonials.link.url,
        textColor: 'text-black',
        bgColor: 'bg-yellow border-yellow',
        hover: 'hover:bg-red active:bg-red hover:border-red active:border-red',
      },
      decorativeSvg: `/.netlify/images?url=${testimonials.decorativeSvg.sourceUrl}`,
      images: testimonials.images.map(image => {
        return {
          src: `/.netlify/images?url=${image.image.sourceUrl}`,
          width: image.image.mediaDetails.width,
          height: image.image.mediaDetails.height,
          alt: image.altText,
        }
      }),
      imageBorderColor: 'border-white',
    },
    videoSectionProps: {
      playButton: `/.netlify/images?url=${videos.playButton.sourceUrl}`,
      videos: videos.videos.map(video => {
        return {
          vimeoId: video.vimeoVideoId,
          bgImg: `/.netlify/images?url=${video.image.sourceUrl}&q=100`,
          context: video.context,
        }
      }),
      rectangleColor: 'bg-yellow',
      videoCTAColor: 'text-white',
      overlayColor: 'bg-black/40',
      contextColor: 'text-orange',
    },
  }
}
export function PartnershipFunction() {
  const data = usePartnershipQuery()
  return {
    title: data.title,
    text: data.text,
    link: {
      rounded: false,
      notLink: false,
      label: data.link.title,
      borderColor: 'border-red',
      fontWeight: 'font-black',
      fontSize: 'text-lg',
      fontFamily: 'font-primary',
      bgColor: 'bg-red',
      textColor: 'text-white',
      hoverBgColor: 'hover:bg-orange',
      hoverBorderColor: 'hover:border-orange',
      hoverTextColor: 'hover:text-white',
      padding: 'py-5 px-0',
      href: data.link.url,
    },
    mobileImg: {
      src: `/.netlify/images?url=${data.mobileImage.sourceUrl}`,
      width: data.mobileImage.mediaDetails.width,
      height: data.mobileImage.mediaDetails.height,
    },
    desktopImg: {
      src: `/.netlify/images?url=${data.desktopImage.sourceUrl}`,
      width: data.desktopImage.mediaDetails.width,
      height: data.desktopImage.mediaDetails.height,
    },
    altText: data.altText,
    bgColor: 'bg-tan',
  }
}
export function BlogsFunction() {
  const { wpPage, allWpPost, wp } = useLatestNewsQuery()
  return {
    backgroundImageMobile: `/.netlify/images?url=${wpPage.latestNews.latestNews.backgroundImageMobile.sourceUrl}&q=100`,
    backgroundImageDesktop: `/.netlify/images?url=${wpPage.latestNews.latestNews.backgroundImageDesktop.sourceUrl}&q=100`,
    topTitleColor: 'text-red',
    titleColor: 'text-black',
    topTitle: wpPage.latestNews.latestNews.title,
    title: wpPage.latestNews.latestNews.subtitle,
    blogData: allWpPost.nodes.map(blog => {
      return {
        link: `/blog/${blog.categories.nodes[0].slug}${blog.link}`,
        image:
          blog.featuredImage !== null
            ? `/.netlify/images?url=${blog.featuredImage.node.sourceUrl}`
            : `/.netlify/images?url=${wp.acfOptionsCommonItems.commonItems.post.heroImageDefault.sourceUrl}`,
        date: blog.date,
        title: blog.title,
        text: blog.excerpt,
      }
    }),
    dateTextColor: 'text-red',
    textColor: 'text-black-light3',
    cardBgColor: 'bg-white',
  }
}

export function BackToTopFunction() {
  return {
    backgroundColor: 'bg-black-light',
  }
}

export function StickyCalloutFunction(handleButton) {
  const data = useStickyCalloutQuery()
  return {
    bgColor: 'bg-white',
    closeTextColor: 'text-[#D9D9D9]',
    textColor: 'text-black',
    price: data.price,
    slashColor: 'border-red',
    title1: data.title1,
    title2: data.title2,
    text: data.text,
    buttonBgColor: 'bg-red',
    buttonTextColor: 'text-white',
    buttonText: data.buttonText,
    onClick: handleButton,
  }
}

export function TestimonialVideosFunction() {
  const { wpPage } = useTestimonialsSectionQuery()
  const videos = wpPage.testimonialsSection.testimonialsSection.videosSection
  return {
    playButton: `/.netlify/images?url=${videos.playButton.sourceUrl}`,
    videos: videos.videos.map(video => {
      return {
        vimeoId: video.vimeoVideoId,
        bgImg: `/.netlify/images?url=${video.image.sourceUrl}&q=100`,
        context: video.context,
      }
    }),
    rectangleColor: 'bg-yellow',
    videoCTAColor: 'text-white',
    overlayColor: 'bg-black/40',
    contextColor: 'text-orange',
  }
}

export function InteriorAccordionsFunction(data) {
  const defaultData = useInteriorAccordionQuery()
  return {
    textColor: 'text-black',
    bgColor: 'bg-white',
    borderColor: 'border-orange',
    contentData: data.wpPage.contentSectionAccordion.contentSectionAccordion.accordion.map(
      accordion => {
        return {
          title: accordion.label,
          content1: accordion.content1,
          content2: accordion.content2,
          image: accordion.image
            ? `/.netlify/images?url=${accordion.image.sourceUrl}`
            : `/.netlify/images?url=${defaultData.accordionDefaultImage.sourceUrl}`,
        }
      },
    ),
    arrowIcon: defaultData.accordionArrow.sourceUrl,
  }
}

export function InteriorCallButton1() {
  const { wp } = useOurServicesQuery()
  return {
    rounded: true,
    twoLabels: true,
    label: wp.acfOptionsCommonItems.commonItems.topCallText,
    label2: wp.acfOptionsCommonItems.commonItems.phoneNumber,
    notLink: false,
    fontWeight: 'font-bold',
    fontSize: 'text-base rfs:lg:text-[1.5rem]',
    fontFamily: 'font-primary',
    textColor: 'text-white',
    label2Color: 'text-white',
    borderColor: 'border-red',
    bgColor: 'bg-red',
    hoverBgColor: 'hover:bg-yellow',
    hoverBorderColor: 'hover:border-yellow',
    hoverTextColor: 'hover:text-white',
    padding: 'rfs:py-5',
    href: `tel:${wp.acfOptionsCommonItems.commonItems.phoneNumber}`,
    phoneIcon: true,
    iconColor: '#FFF',
  }
}
